import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import PageHero from "./PageHero";

// Image Assets
import bgHeroImg from "../assets/image6.jpg";
import visionImg from "../assets/vision.jpg";
import missionImg from "../assets/mission.jpg";
import historyBg from "../assets/history.jpg";
import passport from "../assets/passport.jpg";

// React Icons
import { 
    FaTwitter, 
    FaLinkedin, 
    FaEnvelope, 
    FaSeedling, 
    FaGraduationCap, 
    FaUsers, 
    FaBalanceScale, 
    FaHandHoldingHeart, 
    FaLightbulb,
    FaCheckCircle,
    FaQuoteLeft
} from "react-icons/fa";


export default function AboutUsContent(props) {
    const appContext = useContext(AppContext);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        setReady(true);
    }, []);

    // MWADEO Leadership Array (Direct Injection)
    const teamMembers = [
        {
            role: "Chairperson",
            title: "Board Chair & Head of Governance",
            name: "Office of the Chairperson",
            desc: "Leads the Board of Directors, presides over General Assembly meetings, and provides overarching strategic direction for MWADEO across Tanzania.",
            image: passport
        },
        {
            role: "Executive Secretary",
            name: "Office of the Secretary",
            title: "Chief Executive & Administrator",
            desc: "Directs organizational operations, manages institutional correspondence, coordinates board actions, and oversees project execution.",
            image: passport
        },
        {
            role: "Treasurer",
            name: "Office of the Treasurer",
            title: "Head of Finance & Stewardship",
            desc: "Guarantees fiscal transparency, manages organization budgets, maintains audited account records, and ensures regulatory compliance.",
            image: passport
        }
    ];

    // Core Values Array (6 pillars derived from Constitution)
    const coreValues = [
        {
            icon: <FaSeedling size={24} />,
            title: "Agricultural Innovation",
            desc: "Equipping smallholder farmers with modern, climate-smart techniques to secure food and livelihoods."
        },
        {
            icon: <FaGraduationCap size={24} />,
            title: "Practical Education",
            desc: "Fostering practical skills, vocational capacity, and research to break generational poverty."
        },
        {
            icon: <FaUsers size={24} />,
            title: "Inclusive Empowerment",
            desc: "Prioritizing vulnerable, underprivileged groups including women, youth, and marginalized communities."
        },
        {
            icon: <FaBalanceScale size={24} />,
            title: "Integrity & Governance",
            desc: "Upholding absolute accountability, financial transparency, and constitutional stewardship."
        },
        {
            icon: <FaHandHoldingHeart size={24} />,
            title: "Climate Resilience",
            desc: "Building community mitigation and adaptation strategies against environmental changes."
        },
        {
            icon: <FaLightbulb size={24} />,
            title: "Economic Self-Reliance",
            desc: "Building local capacity so communities generate sustainable income and improved standards of living."
        }
    ];

    if (!ready) {
        return (
            <div className="container mSupportLoading d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                {getInlineLoader()}
            </div>
        );
    }

    return (
        <div className="mwadeo-about-wrapper">
            {/* 1. PAGE HERO */}
            <PageHero 
                kicker="ABOUT US" 
                title="MWANANCHI DEVELOPMENT ORGANIZATION (MWADEO)" 
                bgImage={bgHeroImg} 
            />

            {/* 2. SECTION: VISION & MISSION (Moved to 2nd position) */}
            <section className="about-vm-section py-5">
                <div className="container py-4">
                    <div className="text-center mb-5 max-w-700 mx-auto">
                        <span className="text-uppercase title-font-semi-bold text-muted small letter-spacing-2">Foundational Pillars</span>
                        <h2 className="title-font-bold text-dark text-uppercase mt-1 mb-2">Our Vision & Mission</h2>
                        <div className="heading-line mx-auto"></div>
                    </div>

                    <div className="row g-4 align-items-stretch">
                        {/* Vision Card */}
                        <div className="col-12 col-md-6">
                            <div className="vm-asym-card h-100 p-4 p-lg-5 rounded-4 border bg-white shadow-sm d-flex flex-column justify-content-between position-relative overflow-hidden">
                                <div className="vm-card-top position-relative z-2">
                                    <div className="vm-icon-bubble bg-primary text-white mb-4">
                                        <FaSeedling size={28} />
                                    </div>
                                    <span className="text-muted text-uppercase fw-bold small letter-spacing-2">Future Goal</span>
                                    <h3 className="title-font-bold primaryColor-text text-uppercase mt-1 mb-3">Our Vision</h3>
                                    <p className="font-regular vm-desc-text lh-lg mb-0">
                                        To lift the standard of living of the poor and promote sustainable health, ensuring a stable, self-reliant, and economically productive community across Tanzania.
                                    </p>
                                </div>
                                <div className="vm-card-accent-image mt-4 rounded-3 overflow-hidden" style={{ backgroundImage: `url(${visionImg})` }}></div>
                            </div>
                        </div>

                        {/* Mission Card */}
                        <div className="col-12 col-md-6">
                            <div className="vm-asym-card h-100 p-4 p-lg-5 rounded-4 border bg-white shadow-sm d-flex flex-column justify-content-between position-relative overflow-hidden">
                                <div className="vm-card-top position-relative z-2">
                                    <div className="vm-icon-bubble bg-secondary text-white mb-4">
                                        <FaGraduationCap size={28} />
                                    </div>
                                    <span className="text-muted text-uppercase fw-bold small letter-spacing-2">Action Strategy</span>
                                    <h3 className="title-font-bold primaryColor-text text-uppercase mt-1 mb-3">Our Mission</h3>
                                    <p className="font-regular vm-desc-text lh-lg mb-0">
                                        To promote and take action in building the capacity of people to reduce poverty, while advocating for quality education, research, practical working skills, modern agricultural practices, and climate change resilience.
                                    </p>
                                </div>
                                <div className="vm-card-accent-image mt-4 rounded-3 overflow-hidden" style={{ backgroundImage: `url(${missionImg})` }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. SECTION: PREAMBLE & ORGANIZATIONAL STORY (Moved to 3rd position with Fading Dots) */}
            <section className="about-story-section py-5 position-relative overflow-hidden">
                {/* Dots with smooth Left-to-Right Fade Mask */}
                <div className="bg-shape-grid position-absolute top-0 start-0 w-100 h-100 pointer-events-none">
                    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <pattern id="dotPattern" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
                                <circle cx="3" cy="3" r="1.8" fill="#ffffff" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#dotPattern)" />
                    </svg>
                </div>

                <div className="container position-relative z-2 py-4">
                    <div className="row g-5 align-items-center">
                        <div className="col-12 col-lg-6">
                            <div className="story-image-stack position-relative">
                                <div className="story-main-img shadow-lg" style={{ backgroundImage: `url(${historyBg})` }}></div>
                                <div className="story-glass-badge p-4 shadow-sm border border-light border-opacity-25 rounded-3">
                                    <FaQuoteLeft className="text-warning mb-2 opacity-75" size={28} />
                                    <h5 className="text-white title-font-semi-bold m-0 fs-6">
                                        "Lifting standards of living and resilience across Tanzanian communities."
                                    </h5>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-6">
                            <div className="story-text-content">
                                <span className="badge bg-secondary text-white text-uppercase letter-spacing-2 px-3 py-2 mb-3">Our Context & Purpose</span>
                                <h2 className="title-font-bold text-white text-uppercase display-6 mb-4">
                                    Addressing Poverty & Climate Challenges
                                </h2>
                                <p className="story-text-p font-regular lh-lg mb-4">
                                    In Tanzania, many citizens face significant economic hardships due to limited access to practical education, capital, and modern skills. Furthermore, smallholder farmers—who form the backbone of local livelihoods—face compounding difficulties as climate change intensifies.
                                </p>
                                <p className="story-text-p font-regular lh-lg mb-4">
                                    <strong>MWANANCHI DEVELOPMENT ORGANIZATION (MWADEO)</strong> was established under NGO Act No. 24 of 2002 (Reg No. NGO 24/2002) as a national non-governmental initiative committed to reversing these vulnerabilities through targeted capacity building and sustainable development.
                                </p>
                                
                                <div className="row g-3 mt-2">
                                    <div className="col-6">
                                        <div className="d-flex align-items-center gap-2 text-white">
                                            <FaCheckCircle className="text-warning" />
                                            <span className="font-regular small text-white-90">National Scope</span>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="d-flex align-items-center gap-2 text-white">
                                            <FaCheckCircle className="text-warning" />
                                            <span className="font-regular small text-white-90">Community Driven</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. SECTION: CORE VALUES */}
            <section className="about-values-section py-5">
                <div className="container py-4">
                    <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between mb-5">
                        <div>
                            <span className="text-uppercase title-font-semi-bold text-muted small letter-spacing-2">Guiding Principles</span>
                            <h2 className="title-font-bold text-dark text-uppercase mt-1 mb-0">Our Core Values</h2>
                        </div>
                    </div>

                    <div className="row g-4">
                        {coreValues.map((val, idx) => (
                            <div className="col-12 col-md-6 col-lg-4" key={idx}>
                                <div className="value-modern-box p-4 rounded-4 bg-white border h-100 d-flex flex-column justify-content-between">
                                    <div>
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <div className="value-icon-wrap primaryColor-text">
                                                {val.icon}
                                            </div>
                                            <span className="value-number title-font-bold text-muted opacity-25">0{idx + 1}</span>
                                        </div>
                                        <h5 className="title-font-semi-bold text-dark mb-2">{val.title}</h5>
                                        <p className="font-regular text-muted small mb-0 lh-base">{val.desc}</p>
                                    </div>
                                    <div className="value-card-bottom-line mt-3"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 5. SECTION: GOVERNANCE & TEAM */}
            <section className="about-team-section py-5 position-relative overflow-hidden">
                <div className="team-bg-graphic position-absolute pointer-events-none"></div>

                <div className="container position-relative z-2 py-4">
                    <div className="text-center mb-5 max-w-700 mx-auto">
                        <span className="badge bg-primary text-white text-uppercase px-3 py-2 mb-2">Leadership</span>
                        <h2 className="title-font-bold text-dark text-uppercase">Organizational Governance</h2>
                        <p className="text-muted font-regular small m-0">Constitutional Office Bearers responsible for MWADEO's administration and accountability.</p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {teamMembers.map((member, index) => (
                            <div className="col-12 col-md-4" key={index}>
                                <div className="team-floating-card h-100 p-4 rounded-4 bg-white border d-flex flex-column justify-content-between text-center">
                                    <div>
                                        <div className="team-avatar-frame mx-auto mb-3 position-relative">
                                            <div className="team-avatar-img rounded-circle" style={{ backgroundImage: `url(${member.image})` }}></div>
                                            <span className="team-role-tag badge bg-dark text-white text-uppercase position-absolute bottom-0 start-50 translate-middle-x px-3 py-1">
                                                {member.role}
                                            </span>
                                        </div>
                                        <h4 className="title-font-bold primaryColor-text mb-1 mt-3">{member.name}</h4>
                                        <span className="text-muted font-regular small d-block mb-3 fw-bold">{member.title}</span>
                                        <p className="font-regular team-card-desc small lh-base mb-4 px-2">
                                            {member.desc}
                                        </p>
                                    </div>

                                    <div className="team-social-footer pt-3 border-top d-flex justify-content-center gap-3">
                                        <a href="#" className="team-social-btn" aria-label="Twitter"><FaTwitter /></a>
                                        <a href="#" className="team-social-btn" aria-label="LinkedIn"><FaLinkedin /></a>
                                        <a href="#" className="team-social-btn" aria-label="Email"><FaEnvelope /></a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}